'use client';
import React, { useState } from 'react';
import ClientNav from '@/components/ClientNav';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { CalendarDays, Clock, Car, Wrench, ChevronRight, X, RotateCcw, AlertCircle, Search, Loader2 } from 'lucide-react';
import { sendRescheduleAlert } from '@/lib/emailService';
import { bookingsService, type Booking } from '@/lib/supabase/bookingDataService';

function formatDate(dateStr: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function isUpcoming(dateStr: string) {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dateStr + 'T00:00:00') >= today;
}

type ModalType = 'details' | 'reschedule' | 'cancel' | null;

export default function MyBookingsPage() {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [cancelConfirmed, setCancelConfirmed] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleSubmitted, setRescheduleSubmitted] = useState(false);
  const [clientEmail, setClientEmail] = useState('');

  // Email lookup state
  const [lookupEmail, setLookupEmail] = useState('');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookupEmail.trim()) return;
    setIsLoading(true);
    setSearchError(null);
    try {
      const data = await bookingsService.getByEmail(lookupEmail.trim().toLowerCase());
      setBookings(data);
      setHasSearched(true);
    } catch (err: any) {
      setSearchError('Failed to load bookings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const upcoming = bookings.filter(
    (b) => isUpcoming(b.date) && b.status !== 'cancelled'
  );
  const past = bookings.filter(
    (b) => !isUpcoming(b.date) || b.status === 'cancelled'
  );

  const displayed = activeTab === 'upcoming' ? upcoming : past;

  function openModal(booking: Booking, type: ModalType) {
    setSelectedBooking(booking);
    setModalType(type);
    setCancelConfirmed(false);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleSubmitted(false);
    setClientEmail('');
  }

  function closeModal() {
    setModalType(null);
    setSelectedBooking(null);
  }

  const paymentStatusLabel = (status: string) => {
    if (status === 'succeeded') return 'paid-full';
    if (status === 'pending') return 'unpaid';
    return status;
  };

  return (
    <div className="min-h-screen bg-background">
      <ClientNav />
      <main className="pt-24 pb-16 px-4 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground mb-1">My Bookings</h1>
          <p className="text-sm text-muted-foreground">View, reschedule, or cancel your appointments.</p>
        </div>

        {/* Email Lookup */}
        <form onSubmit={handleLookup} className="mb-8">
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm font-semibold text-foreground mb-3">Look up your bookings</p>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  placeholder="Enter your booking email address"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-background border border-input text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading || !lookupEmail.trim()}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg gold-gradient text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Search
              </button>
            </div>
            {searchError && (
              <p className="mt-2 text-xs text-red-400">{searchError}</p>
            )}
          </div>
        </form>

        {/* Results */}
        {hasSearched && (
          <>
            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-secondary rounded-xl mb-6 w-fit">
              {(['upcoming', 'past'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-150 capitalize ${
                    activeTab === tab
                      ? 'bg-card text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab}
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {tab === 'upcoming' ? upcoming.length : past.length}
                  </span>
                </button>
              ))}
            </div>

            {/* Booking Cards */}
            {displayed.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <CalendarDays size={40} className="text-muted-foreground mb-3 opacity-40" />
                <p className="text-muted-foreground text-sm">No {activeTab} bookings found for {lookupEmail}.</p>
                {activeTab === 'upcoming' && (
                  <a
                    href="/booking-flow"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg gold-gradient text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all"
                  >
                    Book a Service
                  </a>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {displayed.map((booking) => (
                  <div
                    key={booking.id}
                    className="bg-card border border-border rounded-xl p-5 card-hover cursor-pointer group"
                    onClick={() => openModal(booking, 'details')}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Service + Status */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-foreground truncate">{booking.service}</span>
                          <StatusBadge type="booking" status={booking.status} size="sm" />
                          <StatusBadge type="payment" status={paymentStatusLabel(booking.paymentStatus) as any} size="sm" />
                        </div>

                        {/* Meta row */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CalendarDays size={12} />
                            {formatDate(booking.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {booking.time}
                          </span>
                          <span className="flex items-center gap-1">
                            <Car size={12} />
                            {booking.vehicleYear} {booking.vehicleMake} {booking.vehicleModel}
                          </span>
                        </div>

                        {/* Add-ons */}
                        {booking.addons?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {booking.addons.map((a) => (
                              <span key={a} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                                + {a}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Price + Actions */}
                      <div className="flex flex-col items-end gap-3 shrink-0">
                        <span className="text-base font-bold gold-text">${booking.amount.toLocaleString()}</span>
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          {activeTab === 'upcoming' && booking.status !== 'cancelled' && (
                            <>
                              <button
                                onClick={() => openModal(booking, 'reschedule')}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted hover:bg-muted/80 text-foreground border border-border transition-all duration-150 active:scale-95"
                              >
                                <RotateCcw size={11} />
                                Reschedule
                              </button>
                              <button
                                onClick={() => openModal(booking, 'cancel')}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-all duration-150 active:scale-95"
                              >
                                <X size={11} />
                                Cancel
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => openModal(booking, 'details')}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150"
                          >
                            <ChevronRight size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* ── Details Modal ── */}
      <Modal isOpen={modalType === 'details'} onClose={closeModal} title={`Booking ${selectedBooking?.bookingRef}`} size="lg">
        {selectedBooking && (
          <div className="space-y-5">
            {/* Status row */}
            <div className="flex flex-wrap gap-2">
              <StatusBadge type="booking" status={selectedBooking.status} />
              <StatusBadge type="payment" status={paymentStatusLabel(selectedBooking.paymentStatus) as any} />
            </div>

            {/* Service */}
            <div className="bg-secondary rounded-xl p-4 space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Service</p>
              <p className="text-sm font-semibold text-foreground">{selectedBooking.service}</p>
            </div>

            {/* Date/Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-secondary rounded-xl p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-1.5">Date</p>
                <div className="flex items-center gap-1.5 text-sm text-foreground font-medium">
                  <CalendarDays size={14} className="text-primary" />
                  {formatDate(selectedBooking.date)}
                </div>
              </div>
              <div className="bg-secondary rounded-xl p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-1.5">Time</p>
                <div className="flex items-center gap-1.5 text-sm text-foreground font-medium">
                  <Clock size={14} className="text-primary" />
                  {selectedBooking.time}
                </div>
              </div>
            </div>

            {/* Vehicle */}
            <div className="bg-secondary rounded-xl p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Vehicle</p>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Car size={14} className="text-primary shrink-0" />
                <span>{selectedBooking.vehicleYear} {selectedBooking.vehicleMake} {selectedBooking.vehicleModel}{selectedBooking.vehicleColor ? ` · ${selectedBooking.vehicleColor}` : ''}</span>
              </div>
            </div>

            {/* Add-ons */}
            {selectedBooking.addons?.length > 0 && (
              <div className="bg-secondary rounded-xl p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Add-Ons</p>
                <div className="space-y-1.5">
                  {selectedBooking.addons.map((a) => (
                    <div key={a} className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Wrench size={12} />
                      {a}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing */}
            <div className="bg-secondary rounded-xl p-4 space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Pricing</p>
              <div className="border-t border-border pt-2 flex justify-between text-sm font-semibold text-foreground">
                <span>Amount Paid</span>
                <span className="gold-text">${selectedBooking.amount.toLocaleString()}</span>
              </div>
              {selectedBooking.paymentType === 'deposit' && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Payment type</span>
                  <span>30% deposit</span>
                </div>
              )}
            </div>

            {/* Notes */}
            {selectedBooking.notes && (
              <div className="bg-secondary rounded-xl p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-1.5">Notes</p>
                <p className="text-sm text-muted-foreground">{selectedBooking.notes}</p>
              </div>
            )}

            {/* Actions */}
            {isUpcoming(selectedBooking.date) && selectedBooking.status !== 'cancelled' && (
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => { closeModal(); setTimeout(() => openModal(selectedBooking, 'reschedule'), 50); }}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-sm font-medium border border-border transition-all duration-150 active:scale-95"
                >
                  <RotateCcw size={14} />
                  Reschedule
                </button>
                <button
                  onClick={() => { closeModal(); setTimeout(() => openModal(selectedBooking, 'cancel'), 50); }}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-medium border border-red-500/20 transition-all duration-150 active:scale-95"
                >
                  <X size={14} />
                  Cancel Booking
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Reschedule Modal ── */}
      <Modal isOpen={modalType === 'reschedule'} onClose={closeModal} title="Reschedule Booking" size="md">
        {selectedBooking && (
          <div className="space-y-5">
            {rescheduleSubmitted ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
                  <CalendarDays size={22} className="text-emerald-400" />
                </div>
                <p className="text-sm font-semibold text-foreground mb-1">Request Submitted</p>
                <p className="text-xs text-muted-foreground">We&apos;ll confirm your new appointment time shortly.</p>
                <button onClick={closeModal} className="mt-5 px-5 py-2 rounded-lg gold-gradient text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all active:scale-95">
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="bg-secondary rounded-xl p-4">
                  <p className="text-xs text-muted-foreground mb-0.5">Current appointment</p>
                  <p className="text-sm font-medium text-foreground">{formatDate(selectedBooking.date)} at {selectedBooking.time}</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Your Email (for confirmation)</label>
                    <input
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Preferred New Date</label>
                    <input
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Preferred Time</label>
                    <select
                      value={rescheduleTime}
                      onChange={(e) => setRescheduleTime(e.target.value)}
                      className="w-full bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="">Select a time</option>
                      {['8:00 AM','9:00 AM','10:00 AM','11:00 AM','12:00 PM','1:00 PM','2:00 PM','3:00 PM'].map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Reschedule requests are subject to availability. We&apos;ll reach out to confirm your new time.</p>
                <div className="flex gap-3 pt-1">
                  <button onClick={closeModal} className="flex-1 py-2.5 rounded-lg bg-muted text-foreground text-sm font-medium border border-border hover:bg-muted/80 transition-all active:scale-95">
                    Cancel
                  </button>
                  <button
                    disabled={!rescheduleDate || !rescheduleTime}
                    onClick={async () => {
                      setRescheduleSubmitted(true);
                      if (selectedBooking && clientEmail) {
                        await sendRescheduleAlert({
                          clientName: selectedBooking.client,
                          clientEmail,
                          bookingId: selectedBooking.bookingRef,
                          service: selectedBooking.service,
                          oldDate: formatDate(selectedBooking.date),
                          oldTime: selectedBooking.time,
                          newDate: rescheduleDate,
                          newTime: rescheduleTime,
                        });
                      }
                    }}
                    className="flex-1 py-2.5 rounded-lg gold-gradient text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Submit Request
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── Cancel Modal ── */}
      <Modal isOpen={modalType === 'cancel'} onClose={closeModal} title="Cancel Booking" size="sm">
        {selectedBooking && (
          <div className="space-y-5">
            {cancelConfirmed ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-3">
                  <X size={22} className="text-red-400" />
                </div>
                <p className="text-sm font-semibold text-foreground mb-1">Cancellation Requested</p>
                <p className="text-xs text-muted-foreground">Your request for booking {selectedBooking.bookingRef} has been submitted. Refund processing may take 3–5 business days.</p>
                <button onClick={closeModal} className="mt-5 px-5 py-2 rounded-lg bg-muted text-foreground text-sm font-medium border border-border hover:bg-muted/80 transition-all active:scale-95">
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-400 mb-0.5">Are you sure?</p>
                    <p className="text-xs text-muted-foreground">This will cancel your <span className="text-foreground font-medium">{selectedBooking.service}</span> on {formatDate(selectedBooking.date)}.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={closeModal} className="flex-1 py-2.5 rounded-lg bg-muted text-foreground text-sm font-medium border border-border hover:bg-muted/80 transition-all active:scale-95">
                    Keep Booking
                  </button>
                  <button
                    onClick={async () => {
                      await bookingsService.updateStatus(selectedBooking.id, 'cancelled');
                      setBookings((prev) => prev.map((b) => b.id === selectedBooking.id ? { ...b, status: 'cancelled' } : b));
                      setCancelConfirmed(true);
                    }}
                    className="flex-1 py-2.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400 text-sm font-semibold border border-red-500/30 transition-all active:scale-95"
                  >
                    Yes, Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
